import { DateTime } from 'luxon'

import { DatePicker } from '@/components/ui'

interface Params {
  date?: string | Date
  onChange: ({ date }: { date?: string }) => void
  help?: React.ReactNode
  label?: string
  showTime?: boolean
}

/** Coerce a string|Date|unknown into a parseable ISO string, or null if empty/invalid. */
function parseInitialDate(value: unknown): Date | null {
  if (value == null) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'string') {
    if (!value.trim()) return null
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  return null
}

export const DateSelect: React.FC<Params> = ({ date, onChange, help, label, showTime }) => {
  // Calculate 30 days ago for the min date
  const thirteenMonthsAgo = new Date()
  thirteenMonthsAgo.setMonth(thirteenMonthsAgo.getMonth() - 13)
  const minDate = thirteenMonthsAgo.toISOString().slice(0, 10)

  // Today's date as max
  const maxDate = new Date().toISOString().slice(0, 10)

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-4">
        <DatePicker
          initialDate={parseInitialDate(date)}
          label={label || 'Date'}
          shouldShowTime={showTime}
          handleSet={(d: Date | null) => {
            let newDate = ''
            if (d) {
              if (showTime) {
                // Emit the literal wall-clock the user picked, with NO timezone.
                // The server applies the user's profile timezone to a zoneless
                // value and the chart renders in that same zone, so the picked
                // digits (e.g. 00:00) are preserved regardless of the user's zone.
                newDate = DateTime.fromJSDate(d).toFormat("yyyy-MM-dd'T'HH:mm:ss")
              } else {
                newDate = d.toISOString().slice(0, 10)
              }
            }
            onChange({ date: newDate })
          }}
          placeHolderText="Choose the date"
          minDate={minDate}
          maxDate={maxDate}
        />
      </div>
      {help && <span className="text-sm font-light text-base-content">{help}</span>}
    </div>
  )
}
